"use client";

import * as React from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Dialog, DialogClose, DialogContent, DialogTitle } from "@/ui/components/ui/dialog";
import { Button } from "@/ui/components/ui/button";
import { cn } from "@/lib/utils";
import type { ImageCarouselImage } from "@/ui/components/ui/image-carousel";
import { resolveImageAlt } from "@/lib/images/resolve-image-alt";

export interface ProductImageLightboxProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	images: ImageCarouselImage[];
	initialIndex: number;
	productName: string;
}

/**
 * Full-screen modal with enlarged product images; prev/next when multiple images.
 * Keyboard: ArrowLeft/ArrowRight to navigate, Escape closes (Radix Dialog).
 */
export function ProductImageLightbox({
	open,
	onOpenChange,
	images,
	initialIndex,
	productName,
}: ProductImageLightboxProps) {
	const [currentIndex, setCurrentIndex] = React.useState(initialIndex);

	// Snap to the tapped image whenever the lightbox (re)opens or the caller moves
	// the anchor index - adjusted during render (react.dev pattern), no extra commit.
	const [prevSync, setPrevSync] = React.useState({ open, initialIndex });
	if (open !== prevSync.open || initialIndex !== prevSync.initialIndex) {
		setPrevSync({ open, initialIndex });
		if (open) {
			setCurrentIndex(initialIndex);
		}
	}

	const count = images.length;
	const safeIndex = clampIndex(currentIndex, count);
	const current = images[safeIndex];

	React.useEffect(() => {
		if (!open || count <= 1) return;

		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "ArrowLeft") {
				e.preventDefault();
				setCurrentIndex((i) => (i - 1 + count) % count);
			} else if (e.key === "ArrowRight") {
				e.preventDefault();
				setCurrentIndex((i) => (i + 1) % count);
			}
		};

		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [open, count]);

	if (!current) {
		return null;
	}

	const goPrev = () => setCurrentIndex((i) => (i - 1 + count) % count);
	const goNext = () => setCurrentIndex((i) => (i + 1) % count);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				aria-describedby={undefined}
				overlayClassName="bg-black/90 backdrop-blur-none"
				style={{ "--tw-enter-scale": "1", "--tw-exit-scale": "1" } as React.CSSProperties}
				className={cn(
					"fixed inset-0 left-0 top-0 z-50 flex h-[100dvh] max-h-[100dvh] w-full max-w-none",
					"translate-x-0 translate-y-0 flex-col gap-0 border-none bg-transparent p-0 shadow-none",
					"data-[state=closed]:slide-out-to-left-0 data-[state=closed]:slide-out-to-top-0",
					"data-[state=open]:slide-in-from-left-0 data-[state=open]:slide-in-from-top-0",
					"data-[state=closed]:zoom-out-100 data-[state=open]:zoom-in-100",
					"sm:rounded-none",
				)}
				onPointerDownOutside={(e) => e.preventDefault()}
				onInteractOutside={(e) => e.preventDefault()}
			>
				<DialogTitle className="sr-only">{`${productName} ${safeIndex + 1}/${count}`}</DialogTitle>

				<button
					type="button"
					className="absolute inset-0 z-0 cursor-default bg-transparent"
					aria-hidden
					onClick={() => onOpenChange(false)}
				/>

				<div className="pointer-events-none relative z-10 flex h-full min-h-0 flex-col pt-[env(safe-area-inset-top)]">
					<div className="pointer-events-auto absolute right-4 top-[calc(env(safe-area-inset-top,16px)+12px)] z-20 md:right-6">
						<DialogClose asChild>
							<Button
								type="button"
								variant="ghost"
								className="h-11 w-11 bg-white/90 text-black shadow-lg hover:bg-white"
							>
								<span className="sr-only">Close</span>
								<X className="h-6 w-6" aria-hidden />
							</Button>
						</DialogClose>
					</div>

					<div className="pointer-events-auto relative flex min-h-0 flex-1 items-center justify-center px-4 pb-24 pt-16 md:px-12 md:pb-28 md:pt-20">
						<div className="relative h-full max-h-[min(85dvh,900px)] min-h-[200px] w-full">
							<Image
								src={current.url}
								alt={resolveImageAlt(current.alt, productName)}
								fill
								className="object-contain object-center"
								sizes="100vw"
							/>
						</div>
					</div>

					{count > 1 && (
						<>
							<div className="bg-background/80 pointer-events-auto absolute bottom-6 left-1/2 z-20 -translate-x-1/2 rounded-full px-3 py-1 text-sm text-muted-foreground md:bottom-8">
								{safeIndex + 1} / {count}
							</div>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								className="bg-background/80 pointer-events-auto absolute left-2 top-1/2 z-20 h-12 w-12 -translate-y-1/2 border border-border md:left-6 md:h-14 md:w-14"
								onClick={(e) => {
									e.stopPropagation();
									goPrev();
								}}
								aria-label="Previous image"
							>
								<ChevronLeft className="h-8 w-8" />
							</Button>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								className="bg-background/80 pointer-events-auto absolute right-2 top-1/2 z-20 h-12 w-12 -translate-y-1/2 border border-border md:right-6 md:h-14 md:w-14"
								onClick={(e) => {
									e.stopPropagation();
									goNext();
								}}
								aria-label="Next image"
							>
								<ChevronRight className="h-8 w-8" />
							</Button>
						</>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}

function clampIndex(index: number, length: number): number {
	if (length <= 0) return 0;
	if (index < 0) return 0;
	if (index >= length) return length - 1;
	return index;
}
